/**
 * GWP free-gift picker — enrichment layer over the Kite free-gift widget.
 *
 * Kite renders the SAME inner markup into two DISTINCT native wrappers on a
 * product page (so they never collide — see cart-drawer.liquid / main-product.liquid):
 *   • cart  → `.gfgCartPageWrapperV2`  — pre-placed in [data-cart-gwp-mount] inside a
 *             server-rendered `.noted-gwp` at the top of the basket.
 *   • PDP   → `.gfgProductPageWrapperV2` — auto-injected by Kite just above the
 *             Add-to-Basket button. We tag it `.noted-gwp` here so the same skin
 *             (assets/cart-gwp.css, scoped to `.noted-gwp`) + enrichment applies.
 *
 * assets/cart-gwp.css skins the structure; this file fills the gaps Kite's markup
 * can't express on its own:
 *   • subtitle  → "Inspired By {vendor}"      (Kite shows "Free Gift ready to claim")
 *   • + the gift's variant line ("50ml")       (not in Kite's row)
 *   • a real product thumbnail                 (Kite's icon is tiny)
 *   • data-gwp-enriched → reveal the subtitle once it's been rewritten
 *   • data-gwp-ready    → reveal the block once Kite has rendered rows
 *   • data-gwp-claimed  → header goes black when a gift is in the cart
 *
 * The +/✓ claim-button glyphs and +/⌄ header toggle are drawn entirely in
 * assets/cart-gwp.css (CSS background-images), so they never blank-flash when
 * Kite re-renders a row — no JS involved.
 *
 * Kite still owns eligibility, the claim action, and the Functions discount.
 * We never re-derive any of that — we read product-ids off whatever rows Kite
 * renders, so any campaign the merchant configures flows through unchanged.
 *
 * Kite adds/removes the gift server-side but emits NO DOM event the theme can
 * hear, so the claim handler below drives the drawer re-render itself: it polls
 * cart.js until the line count moves, then dispatches `cart:change` for
 * assets/cart.js to re-render the drawer; here we also re-skin whenever Kite
 * re-renders its widget.
 */
(function () {
  'use strict';

  // Shopper-facing wording from the theme's language file (snippets/js-strings.liquid).
  function str(key) {
    var s = window.notedStrings && window.notedStrings.gwp;
    return (s && s[key]) || '';
  }
  if (window.__notedGwpInit) return;
  window.__notedGwpInit = true;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * The EXACT gift Kite will add for this row, straight off Kite's own campaign
   * config (assets/gwp-claim.js) — never guessed from the product's variant
   * list. Kite's rendered row only exposes `product-id`, so the theme used to
   * fetch the product JSON and take the first `\d+ml` variant, which is the 5ml
   * in option order while the campaign gives away the 50ml.
   *
   * @param {string} productId
   * @returns {{variantId, variant, image, title}|null}
   */
  function kiteGift(productId) {
    var api = window.NotedGwpClaim;
    return api ? api.giftForProduct(productId) : null;
  }

  // product-id → { vendor, variantTitle, image } (resolved once, reused)
  var cache = {};

  function fetchProduct(productId) {
    if (cache[productId]) return Promise.resolve(cache[productId]);
    // id → handle via predictive search, then the product JSON (same hop the
    // 1.1 kite-vendor-display.js used — Kite rows only expose product-id).
    return fetch(root() + 'search/suggest.json?q=id:' + productId + '&resources[type]=product&limit=1')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var p = data.resources && data.resources.results && data.resources.results.products && data.resources.results.products[0];
        if (!p || !p.handle) throw new Error('no handle for ' + productId);
        return fetch(root() + 'products/' + p.handle + '.js').then(function (r) { return r.json(); });
      })
      .then(function (prod) {
        // Vendor ("Inspired By …") only lives on the product — Kite's config
        // doesn't carry it. The variant/image below are last-resort fallbacks
        // for when kiteGift() can't find the row's product in the campaign
        // (e.g. settings not loaded yet); they are a GUESS, so kiteGift() wins.
        var variant = (prod.variants || []).find(function (v) { return /\d+\s*ml/i.test(v.title); }) || prod.variants[0] || {};
        var info = {
          vendor: prod.vendor || '',
          variantTitle: variant.title && variant.title !== 'Default Title' ? variant.title : '',
          image: (prod.featured_image || (prod.images && prod.images[0]) || '')
        };
        cache[productId] = info;
        return info;
      })
      .catch(function () { return { vendor: '', variantTitle: '', image: '' }; });
  }

  // Is this row below the campaign threshold? Kite tags the claim button
  // `.gfgFreeGiftInvalidProductClaimButton` and writes a "£X away to unlock!"
  // subtitle in that state (claimable → `.gfgFreeGiftReadyToClaimButton`).
  function rowLocked(row) {
    var btn = row.querySelector('.gfgFreeGiftClaimButton');
    return !!(btn && btn.classList.contains('gfgFreeGiftInvalidProductClaimButton'));
  }

  function enrichRow(row) {
    var pid = row.getAttribute('product-id') || row.getAttribute('data-product-id');
    if (!pid) return;
    var locked = rowLocked(row);
    var gift = kiteGift(pid);
    // Re-process whenever the product, the lock state (cart crossing the
    // threshold) OR the resolved gift variant changes, so we swap between
    // "£X away to unlock!" and "Inspired By …" — and so a row enriched before
    // Kite's settings landed gets corrected once kiteGift() can resolve it.
    var stateKey = pid + (locked ? ':locked' : ':ready') + ':' + ((gift && gift.variant) || '');
    if (row.getAttribute('data-gwp-state') === stateKey) return;
    row.setAttribute('data-gwp-state', stateKey);
    row.setAttribute('data-gwp-pid', pid);

    if (locked) {
      // Below threshold: keep Kite's "£X away to unlock!" message verbatim (don't
      // overwrite it with "Inspired By"), drop any variant line left from a
      // claimable render, and just reveal the (Kite-authored) subtitle. The
      // disabled-button look is pure CSS (keyed on the invalid-claim class).
      var oldVar = row.querySelector('.gwp-variant');
      if (oldVar) oldVar.remove();
      row.setAttribute('data-gwp-locked', '');
      row.setAttribute('data-gwp-enriched', '');   // CSS reveals Kite's unlock message
      return;
    }

    // Claimable → full enrichment (Inspired By / variant / thumbnail).
    row.removeAttribute('data-gwp-locked');
    if (row.__gwpEnriching) {
      // A fetch for the PREVIOUS state key is still in flight and will write its
      // (now outdated) values. Drop the stamp so the next skin pass re-runs this
      // row against the current state instead of short-circuiting on it.
      row.removeAttribute('data-gwp-state');
      return;
    }
    row.__gwpEnriching = true;

    fetchProduct(pid).then(function (info) {
      row.__gwpEnriching = false;
      // If the cart dropped below threshold while this was in flight, bail — the
      // next skin pass handles the locked state (don't write "Inspired By" over
      // a now-"£X away to unlock!" subtitle).
      if (rowLocked(row)) return;
      // subtitle → "Inspired By {vendor}". Noted's Vendor field already carries
      // the "Inspired By …" prefix, so only add it when it's missing.
      var sub = row.querySelector('.gfgFreeGiftMsgSubTitle');
      if (sub && info.vendor) {
        var v = info.vendor.trim();
        sub.textContent = /^inspired\s+by\b/i.test(v) ? v : str('inspired_by').replace('{vendor}', v);
      }
      // Variant line ("50ml") — Kite's configured gift variant wins; the
      // product-JSON guess is only a fallback. Re-written (not just appended)
      // so a row rendered before Kite's settings landed self-corrects, and so
      // re-pointing the campaign never leaves a stale size on screen.
      var variantTitle = (gift && gift.variant) || info.variantTitle;
      var textContainer = row.querySelector('.gfgFreeGiftMsgTextContainer');
      if (textContainer) {
        var line = textContainer.querySelector('.gwp-variant');
        if (variantTitle) {
          if (!line) {
            line = document.createElement('div');
            line.className = 'gwp-variant';
            textContainer.appendChild(line);
          }
          if (line.textContent !== variantTitle) line.textContent = variantTitle;
        } else if (line) {
          line.remove();
        }
      }
      // Upgrade the thumbnail — Kite's own variant image for the gift variant
      // (the 50ml bottle), falling back to the product's featured image.
      var image = (gift && gift.image) || info.image;
      var img = row.querySelector('.gfgFreeGiftMsgIcon img');
      if (img && image) {
        var src = image.indexOf('//') === 0 ? 'https:' + image : image;
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
      }
      row.setAttribute('data-gwp-enriched', '');   // CSS reveals the (rewritten) subtitle
    });
  }

  /* ─────────────────── strapline (app-owned copy) ─────────────────── */
  // The standing invitation above the picker in the cart drawer. Its wording
  // belongs to the free-gift app, not the theme: the merchant edits the campaign
  // and the basket follows, with no theme deploy and no admin write from here.
  //
  // Read STRAIGHT off Kite's campaign config, specifically the `conditionNotMet`
  // title. It used to be scraped from Kite's rendered header on the way past
  // (the header gets overwritten with the Figma label, so it had to be read
  // before the overwrite) — but Kite swaps that element through THREE titles as
  // the cart moves:
  //
  //   conditionNotMet     "Treat yourself to a FREE 50ml on us (min spend applies)"
  //   conditionInProgress "Please Select Your Free Gift"
  //   conditionFulFilled  "Free Gift Added To Cart"
  //
  // so above the threshold the scrape captured a STATUS line and printed it
  // directly above a button already reading "SELECT YOUR FREE GIFT" — the copy
  // duplicated itself in exactly the states the guard was meant to exclude, and
  // the localStorage cache then replayed it on the next load. Naming the field
  // we want removes the state problem, the cache, and the guesswork together.
  //
  // Via NotedGwpClaim.campaign() so this honours the same market gate as every
  // other GWP surface: a market the campaign excludes gets no strapline rather
  // than an invitation it can't fulfil.
  function kiteStrapline() {
    try {
      var api = window.NotedGwpClaim;
      var c = api && api.campaign();
      var t = c && c.campaign.configuration.globalConfig.conditionNotMet.title;
      t = String(t == null ? '' : t).trim();
      return t || null;
    } catch (e) {
      return null;
    }
  }

  function paintStrapline() {
    var text = kiteStrapline();
    if (!text) return;                    // Kite not ready → leave the server fallback in place
    document.querySelectorAll('[data-gwp-strapline]').forEach(function (el) {
      if (el.textContent.trim() !== text) el.textContent = text;
    });
  }


  /* ─────────────────────────── Kite V2 widget ────────────────────────────────
     Kite moved Noted's gift onto its V2 engine on 28 Sep 2026 (settings in
     gfg.settings.freeGiftsV2, markup `.gfgFGv2-*`). V2 renders into the SAME
     mounts as V1 — the basket's `.gftFreeGiftWrapper` in [data-cart-gwp-mount]
     and the PDP's in [data-pdp-gwp-mount] — so each `.noted-gwp` now holds one
     of two widgets and skin() routes on which one it finds. V1 is untouched.

     As with V1 we only restyle and enrich (assets/cart-gwp.css, V2 section):
     Kite keeps eligibility, the ADD/remove action and the discount Function.
     Kite V2 rows carry no product id, so gifts are matched to Kite's own
     campaign config by title (unique per campaign). */
  function v2GiftByTitle(title) {
    var list = window.gfg && window.gfg.settings && window.gfg.settings.freeGiftsV2;
    if (!Array.isArray(list) || !title) return null;
    var want = String(title).trim().toLowerCase();
    for (var i = 0; i < list.length; i++) {
      var segs = (list[i] && list[i].segmentsData && list[i].segmentsData.segmentsList) || [];
      for (var j = 0; j < segs.length; j++) {
        var rules = (segs[j].getYRulesData && segs[j].getYRulesData.rulesList) || [];
        for (var k = 0; k < rules.length; k++) {
          var vals = (rules[k].ruleValue && rules[k].ruleValue.value) || [];
          for (var m = 0; m < vals.length; m++) {
            var v = vals[m];
            if (v && String(v.title || '').trim().toLowerCase() === want) {
              var variant = (v.variants && v.variants[0]) || {};
              return {
                handle: v.handle,
                variant: variant.variantTitle && variant.variantTitle !== 'Default Title' ? variant.variantTitle : '',
                image: v.image || ''
              };
            }
          }
        }
      }
    }
    return null;
  }

  var vendorCache = {};
  function vendorFor(handle) {
    if (!handle) return Promise.resolve('');
    if (vendorCache[handle] != null) return Promise.resolve(vendorCache[handle]);
    return fetch(root() + 'products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) { vendorCache[handle] = (p && p.vendor) || ''; return vendorCache[handle]; })
      .catch(function () { return ''; });
  }

  function v2RowLocked(row) {
    return row.classList.contains('gfgFGv2-row-locked') || row.classList.contains('gfgFGv2-row-out_of_stock');
  }

  function enrichRowV2(row) {
    var titleEl = row.querySelector('.gfgFGv2-title');
    var info = row.querySelector('.gfgFGv2-info');
    if (!titleEl || !info) return;
    var title = titleEl.textContent.trim();
    var locked = v2RowLocked(row);
    var stateKey = title + (locked ? ':locked' : ':ready');
    if (row.getAttribute('data-gwp-state') === stateKey && row.hasAttribute('data-gwp-enriched')) return;
    row.setAttribute('data-gwp-state', stateKey);
    row.removeAttribute('data-gwp-enriched');

    if (locked) {
      // Below the trigger: keep Kite's own status line ("Buy any item…"),
      // drop our enrichment, reveal.
      row.querySelectorAll('.gwp-sub, .gwp-variant').forEach(function (n) { n.remove(); });
      row.setAttribute('data-gwp-locked', '');
      row.setAttribute('data-gwp-enriched', '');
      return;
    }
    row.removeAttribute('data-gwp-locked');

    var gift = v2GiftByTitle(title);
    // Size line ("5ml") straight from Kite's campaign — the variant it gives away.
    var line = info.querySelector('.gwp-variant');
    if (gift && gift.variant) {
      if (!line) { line = document.createElement('div'); line.className = 'gwp-variant'; info.appendChild(line); }
      if (line.textContent !== gift.variant) line.textContent = gift.variant;
    } else if (line) { line.remove(); }

    vendorFor(gift && gift.handle).then(function (vendor) {
      if (v2RowLocked(row)) return;
      var sub = info.querySelector('.gwp-sub');
      if (vendor) {
        var v = vendor.trim();
        var text = /^inspired\s+by\b/i.test(v) ? v : str('inspired_by').replace('{vendor}', v);
        if (!sub) { sub = document.createElement('div'); sub.className = 'gwp-sub'; titleEl.insertAdjacentElement('afterend', sub); }
        if (sub.textContent !== text) sub.textContent = text;
      }
      row.setAttribute('data-gwp-enriched', '');
    });
  }

  function skinV2(gwp) {
    var rows = gwp.querySelectorAll('.gfgFGv2-gift-row');
    if (!rows.length) { gwp.removeAttribute('data-gwp-ready'); return; }
    gwp.setAttribute('data-gwp-v2', '');
    rows.forEach(enrichRowV2);
    gwp.setAttribute('data-gwp-ready', '');

    // Mid-swap Kite rebuilds the rows between the remove and the add, so for a
    // beat nothing is claimed. A gift IS held throughout a swap: keep the
    // claimed header/tick and re-mark the incoming row (same as the V1 path).
    var busy = gwp.hasAttribute('data-gwp-busy');
    if (busy) {
      var pt = (gwp.getAttribute('data-gwp-pending-title') || '').toLowerCase();
      rows.forEach(function (r) {
        var t = r.querySelector('.gfgFGv2-title');
        if (pt && t && t.textContent.trim().toLowerCase() === pt) r.setAttribute('data-gwp-pending', '');
        else r.removeAttribute('data-gwp-pending');
      });
    }
    var anyClaimed = busy || !!gwp.querySelector('.gfgFGv2-row-added');
    if (anyClaimed) gwp.setAttribute('data-gwp-claimed', ''); else gwp.removeAttribute('data-gwp-claimed');
    var anyUnlocked = !!gwp.querySelector('.gfgFGv2-gift-row:not(.gfgFGv2-row-locked):not(.gfgFGv2-row-out_of_stock)');
    if (anyUnlocked) gwp.setAttribute('data-gwp-unlocked', ''); else gwp.removeAttribute('data-gwp-unlocked');

    // Header copy → Figma, same labels as the V1 picker (CSS uppercases).
    var title = gwp.querySelector('.gfgFGv2-dropdown-title');
    if (title) {
      var want = anyClaimed ? str('gift_added') : str('select_gift');
      if (title.textContent.trim() !== want) title.textContent = want;
    }
    paintStrapline();
  }

  function skin(gwp) {
    if (gwp.querySelector('.gfgFGv2-widget')) { skinV2(gwp); return; }
    var rows = gwp.querySelectorAll('.gfgFreeGiftMsgOptionRow');
    if (!rows.length) { gwp.removeAttribute('data-gwp-ready'); return; }

    rows.forEach(enrichRow);

    // reveal the block + mark claimed (header → black) if any gift is in the cart.
    // The +/✓ and +/⌄ glyphs are pure CSS (keyed on Kite's own classes), so no
    // per-button JS is needed here.
    gwp.setAttribute('data-gwp-ready', '');
    // Mid-swap the old gift is momentarily out of the cart, so Kite reports
    // nothing claimed and the header would flip back to "Select your free gift"
    // for a beat. A gift IS being held throughout a swap — hold the claimed
    // state so the header doesn't flicker between the two labels.
    var anyClaimed = gwp.hasAttribute('data-gwp-busy')
      || !!gwp.querySelector('.gfgFreeGiftClaimButton.gfgFreeGiftActiveFreeGiftClaimButton, .gfgFreeGiftClaimButton[data-gwp-claimed="true"]');
    if (anyClaimed) gwp.setAttribute('data-gwp-claimed', ''); else gwp.removeAttribute('data-gwp-claimed');

    // "On unlock, the select-your-free-gift element turns black" (REQUIREMENTS
    // FF-0611) — qualification is the #1 missed cue. A gift is unlocked once any
    // row is claimable or already claimed (i.e. NOT below-threshold/invalid).
    var anyUnlocked = !!gwp.querySelector('.gfgFreeGiftClaimButton:not(.gfgFreeGiftInvalidProductClaimButton)');
    if (anyUnlocked) gwp.setAttribute('data-gwp-unlocked', ''); else gwp.removeAttribute('data-gwp-unlocked');

    // Header copy → Figma. Kite's configured title ("Get a free gift with your
    // order" / "Free gift added to basket") is overridden in-theme so we never
    // touch the account-global app setting (which would change the live store).
    // CSS uppercases it: "SELECT YOUR FREE GIFT" / "FREE GIFT ADDED TO YOUR CART".
    var header = gwp.querySelector('.gfgFreeGiftSelectDiv, .gfgFreeGiftSelectDivExpanded');
    var title = header && header.querySelector('.gfgFreeGiftMsgTitle');
    if (title) {
      var want = anyClaimed ? str('gift_added') : str('select_gift');
      if (title.textContent.trim() !== want) title.textContent = want;
    }
    paintStrapline();

    // Kite re-renders rows mid-swap; re-apply the pending marker so the spinner
    // stays on the row that was clicked.
    if (gwp.hasAttribute('data-gwp-busy')) markPendingRow(gwp);
  }

  // Kite injects its native PDP wrapper bare into the buy-box (not inside a
  // `.noted-gwp`). Tag it so the `.noted-gwp`-scoped CSS + skin below apply to it
  // exactly like the cart mount. The cart's `.gfgCartPageWrapperV2` is already
  // inside a server-rendered `.noted-gwp`, so it's skipped here.
  function tagWrappers() {
    document.querySelectorAll('.gfgProductPageWrapperV2').forEach(function (w) {
      if (!w.closest('.noted-gwp')) w.classList.add('noted-gwp');
    });
    // Kite V2 prefers a Kite app block (.gfgFreeGiftWrapperV3) when the template
    // has one; skin it there too rather than leave Kite's default look.
    document.querySelectorAll('.gfgFreeGiftWrapperV3').forEach(function (w) {
      if (!w.closest('.noted-gwp') && w.querySelector('.gfgFGv2-widget')) w.classList.add('noted-gwp');
    });
  }

  function skinAll() {
    tagWrappers();
    document.querySelectorAll('.noted-gwp').forEach(skin);
    // Outside skin() as well as inside it: skin() bails early when Kite hasn't
    // rendered rows yet, which is exactly the state a fresh drawer re-render is
    // in — and that re-render has just put the server fallback back on screen.
    paintStrapline();
  }

  // Re-skin whenever Kite (re)renders its widget, and after our own drawer
  // re-renders (which wipe + restore the mount). One observer on the document
  // survives the [data-cart-rerender] innerHTML swap.
  var raf = null;
  function scheduleSkin() {
    if (raf) return;
    raf = requestAnimationFrame(function () { raf = null; skinAll(); });
  }

  // Matches anything we skin: the `.noted-gwp` mounts plus Kite's native PDP
  // wrapper *before* tagWrappers() has tagged it (its first injection wouldn't
  // be inside a `.noted-gwp` yet, so the body observer would otherwise miss it).
  var WATCH_SEL = '.noted-gwp, .gfgProductPageWrapperV2, .gfgFreeGiftWrapperV3';

  function start() {
    paintStrapline();
    skinAll();
    // One document-wide observer covers BOTH placements: the cart drawer mount
    // (restored on every [data-cart-rerender] swap) and the PDP wrapper Kite
    // auto-injects + re-fills. Re-skin only when a mutation actually touches one
    // of WATCH_SEL, so the body-wide subtree observer stays cheap.
    var obs = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        var t = m.target;
        if (t && t.closest && t.closest(WATCH_SEL)) { scheduleSkin(); return; }
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType === 1 && ((n.matches && n.matches(WATCH_SEL)) || (n.querySelector && n.querySelector(WATCH_SEL)))) {
            scheduleSkin(); return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* ── Claim → spinner + self-driven drawer reconcile ────────────────────────
     Kite adds/removes the gift server-side but fires NO event the theme can
     hear (nothing in the theme ever dispatches `cart:change` for it). So we
     drive the re-render ourselves: poll cart.js until the line count moves off
     its pre-click value, then dispatch a bare `cart:change` — cart.js re-renders
     the drawer (surfacing/removing the £0.00 gift line) and clears the spinner.
     A hard cap dispatches anyway, so the drawer always re-syncs and the spinner
     never has to fall back on cart.js's 8s safety timeout. We deliberately don't
     intercept Kite's cart call — Kite still owns the add; we only reconcile. */
  var polling = false, pollDeadline = 0;

  function cartCount() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { return c.item_count; })
      .catch(function () { return null; });
  }

  function reconcileAfterClaim() {
    // Pre-click count straight off the open drawer — instant, captured before
    // Kite mutates. Falls back to the first cart.js read if the badge is absent.
    var badge = document.querySelector('[data-cart-drawer] [data-cart-count]');
    var baseline = badge ? parseInt(badge.getAttribute('data-cart-count'), 10) : NaN;
    pollDeadline = Date.now() + 6000;   // (re)extend the window on every claim
    if (polling) return;                // a poll loop is already running
    polling = true;

    (function tick() {
      cartCount().then(function (count) {
        if (count != null && !isFinite(baseline)) baseline = count;
        var moved = count != null && isFinite(baseline) && count !== baseline;
        if (moved || Date.now() >= pollDeadline) {
          polling = false;
          document.dispatchEvent(new CustomEvent('cart:change')); // → cart.js re-render + spinner off
          return;
        }
        setTimeout(tick, 400);
      });
    })();
  }

  /* ── Swap the claimed gift ─────────────────────────────────────────────────
     The campaign is single-select, and Kite's widget will NOT move the gift:
     with one already held, clicking a different row is a no-op on its side. The
     theme then sat through the full 6s reconcile poll waiting for a cart change
     that was never coming, which read as "it freezes for a while and then
     nothing happens".

     So we take that click over: remove the held gift, then claim the new one
     through Kite's own function (assets/gwp-claim.js owns both halves, and the
     quick-buy picker already worked this way). Kite keeps ownership of the
     line-item properties and the Function discount — we only change WHICH gift
     is held. */
  /* The loading state is the PICKER's, not the drawer's. Firing `cart:adding`
     here washed the whole basket white behind a centred spinner while the
     picker underneath still showed the old gift ticked — the one thing the
     shopper was looking at was the one thing that didn't react. Instead the
     row you picked spins in place and the rest go quiet, so the feedback is
     where the click was. cart.js now carries the picker across the re-render
     (see swapRerender), so it never collapses mid-swap either. */
  var swapping = false;

  function setBusy(gwp, pid) {
    if (!gwp) return;
    if (pid) { gwp.setAttribute('data-gwp-busy', ''); gwp.setAttribute('data-gwp-pending-pid', pid); }
    else { gwp.removeAttribute('data-gwp-busy'); gwp.removeAttribute('data-gwp-pending-pid'); }
    markPendingRow(gwp);
  }

  // Kite re-renders its rows during the swap, which drops any attribute we put
  // on a row. So the pending row is derived from the product id held on the
  // container (which survives) and re-applied on every skin pass.
  function markPendingRow(gwp) {
    var pid = gwp.getAttribute('data-gwp-pending-pid');
    gwp.querySelectorAll('.gfgFreeGiftMsgOptionRow').forEach(function (r) {
      var rid = r.getAttribute('product-id') || r.getAttribute('data-product-id');
      if (pid && String(rid) === String(pid)) r.setAttribute('data-gwp-pending', '');
      else r.removeAttribute('data-gwp-pending');
    });
  }

  /* The gift's CART LINE has to move at the same time as the picker. The swap
     takes about a second (remove, then Kite's claim, then the re-render), and
     for all of it the basket went on showing the OLD gift — you'd click Verona
     and still read "Naples" underneath until the new line arrived. So paint the
     incoming gift straight away, dimmed until it's confirmed. We know exactly
     what's coming (Kite's campaign names the product, variant and image), and
     the re-render overwrites this with the truth either way — including
     removing the line entirely if Kite declined the claim. */
  function previewGiftLine(gift) {
    var line = document.querySelector('[data-cart-drawer] [data-cart-line-gift]');
    if (!line || !gift) return;

    var img = line.querySelector('img');
    if (img && gift.image) {
      // srcset/sizes would out-rank a bare src swap, so clear them first.
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.setAttribute('src', gift.image);
    }
    var title = line.querySelector('[data-cart-line-title]');
    if (title && gift.title) title.textContent = gift.title;
    var option = line.querySelector('[data-cart-line-option]');
    if (option && gift.variant) option.textContent = gift.variant;

    line.setAttribute('data-gift-swapping', '');
  }

  function swapGift(row) {
    if (!row) return false;
    var pid = row.getAttribute('product-id') || row.getAttribute('data-product-id');
    var gift = kiteGift(pid);
    var api = window.NotedGwpClaim;
    // Can't identify the gift (settings not loaded, product not in the
    // campaign) → leave the click alone rather than half-swap. The cart keeps
    // the gift it already has.
    if (!gift || !gift.variantId || !api) return false;

    var gwp = row.closest('.noted-gwp');
    swapping = true;
    setBusy(gwp, pid);
    previewGiftLine(gift);

    api.swapTo(gift.variantId)
      .catch(function (err) { console.error('[cart-gwp] gift swap failed', err); })
      .then(function () {
        swapping = false;
        setBusy(gwp, null);
        // Re-render either way. If Kite declined (off-market, below threshold)
        // the old gift is already gone, and the drawer must never show a gift
        // the cart doesn't hold.
        document.dispatchEvent(new CustomEvent('cart:change'));
      });
    return true;
  }


  /* ── Kite V2: swap the held gift ────────────────────────────────────────────
     V2 allows one gift at a time: once one is in the basket the other rows go
     "disabled" and Kite ignores them. V1's picker let the shopper tap another
     gift to swap straight over (swapGift above), so V2 does the same: tapping a
     disabled row removes the held gift and adds the tapped one, both through
     Kite's own functions (gfg.gfgFreeGiftV2.cart.removeGiftManually /
     addGiftManually), so Kite keeps the line properties and the discount. */
  var swappingV2 = false;

  function v2Idx(el) {
    var b = el && (el.matches && el.matches('[data-gift-index]') ? el : el.querySelector('[data-gift-index]'));
    if (!b) return null;
    var ci = parseInt(b.getAttribute('data-campaign-index'), 10);
    var ri = parseInt(b.getAttribute('data-rule-index'), 10);
    var gi = parseInt(b.getAttribute('data-gift-index'), 10);
    return (isFinite(ci) && isFinite(ri) && isFinite(gi)) ? { ci: ci, ri: ri, gi: gi } : null;
  }

  function v2IdxByTitle(V, from, row) {
    var t = row.querySelector('.gfgFGv2-title');
    var camp = V && from && V.state && V.state.campaigns && V.state.campaigns[from.ci];
    if (!t || !camp) return null;
    var want = t.textContent.trim().toLowerCase();
    var segs = (camp.segmentsData && camp.segmentsData.segmentsList) || [];
    for (var ri = 0; ri < segs.length; ri++) {
      var rule = segs[ri].getYRulesData && segs[ri].getYRulesData.rulesList && segs[ri].getYRulesData.rulesList[0];
      var vals = (rule && rule.ruleValue && rule.ruleValue.value) || [];
      for (var gi = 0; gi < vals.length; gi++) {
        if (String(vals[gi].title || '').trim().toLowerCase() === want) return { ci: from.ci, ri: ri, gi: gi };
      }
    }
    return null;
  }

  function swapGiftV2(row) {
    var V = window.gfg && window.gfg.gfgFreeGiftV2;
    var gwp = row.closest('.noted-gwp');
    var held = gwp && gwp.querySelector('.gfgFGv2-row-added');
    var from = held && v2Idx(held);
    // Kite renders the other rows' buttons WITHOUT their indices while a gift
    // is held ("disabled"), so find the tapped gift in Kite's campaign by title.
    var to = v2Idx(row) || v2IdxByTitle(V, from, row);
    if (!V || !V.cart || !to || !from) return false;
    var camp = V.state && V.state.campaigns && V.state.campaigns[to.ci];
    if (!camp || typeof V.cart.removeGiftManually !== 'function' || typeof V.cart.addGiftManually !== 'function') return false;

    // Variant Kite gives away for this gift (single-variant 5ml today).
    var seg = camp.segmentsData && camp.segmentsData.segmentsList && camp.segmentsData.segmentsList[to.ri];
    var vals = seg && seg.getYRulesData && seg.getYRulesData.rulesList && seg.getYRulesData.rulesList[0] &&
      seg.getYRulesData.rulesList[0].ruleValue && seg.getYRulesData.rulesList[0].ruleValue.value;
    var prod = vals && vals[to.gi];
    var vid = (V.state.selectedVariants && V.state.selectedVariants[to.ci + '_' + to.ri + '_' + to.gi]) ||
      (prod && prod.variants && prod.variants[0] && prod.variants[0].variantId) || undefined;

    swappingV2 = true;
    var t = row.querySelector('.gfgFGv2-title');
    gwp.setAttribute('data-gwp-busy', '');
    if (t) gwp.setAttribute('data-gwp-pending-title', t.textContent.trim());
    row.setAttribute('data-gwp-pending', '');
    var g = t && v2GiftByTitle(t.textContent);
    if (g) previewGiftLine({ image: g.image, title: t.textContent.trim(), variant: g.variant });

    Promise.resolve(V.cart.removeGiftManually(camp, from.ci, from.ri, from.gi))
      .then(function () { return V.cart.addGiftManually(camp, to.ci, to.ri, to.gi, vid); })
      .catch(function (err) { console.error('[cart-gwp] V2 gift swap failed', err); })
      .then(function () {
        swappingV2 = false;
        gwp.removeAttribute('data-gwp-busy');
        gwp.removeAttribute('data-gwp-pending-title');
        gwp.querySelectorAll('[data-gwp-pending]').forEach(function (r) { r.removeAttribute('data-gwp-pending'); });
        skinV2(gwp);
        document.dispatchEvent(new CustomEvent('cart:change'));
      });
    return true;
  }

  document.addEventListener('click', function (e) {
    var row = e.target.closest('.noted-gwp .gfgFGv2-row-disabled');
    if (!row) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (swappingV2) return;
    swapGiftV2(row);
  }, true);

  // Kite V2: its own ADD / remove buttons claim and unclaim. Like V1 it emits
  // nothing the theme hears, so reconcile the drawer the same way.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.noted-gwp [data-action="add-gift"], .noted-gwp [data-action="remove-gift"]');
    if (!btn || btn.disabled) return;
    if (btn.getAttribute('data-action') === 'add-gift') document.dispatchEvent(new CustomEvent('cart:adding'));
    reconcileAfterClaim();
  }, true);

  // Capture phase so we run even though Kite handles the same click.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.gfgFreeGiftClaimButton');
    if (!btn || !btn.closest('.noted-gwp')) return;
    // Below threshold the gift isn't claimable — Kite no-ops the click, so don't
    // open the drawer with a spinner / kick off a reconcile for nothing.
    if (btn.classList.contains('gfgFreeGiftInvalidProductClaimButton')) return;
    if (swapping) { e.preventDefault(); e.stopImmediatePropagation(); return; }

    var gwp = btn.closest('.noted-gwp');
    var isThis = btn.classList.contains('gfgFreeGiftActiveFreeGiftClaimButton');
    var otherHeld = !isThis && !!gwp.querySelector('.gfgFreeGiftClaimButton.gfgFreeGiftActiveFreeGiftClaimButton');

    // A DIFFERENT gift is held → this is a swap, which Kite won't do. Take the
    // click before Kite's own handler sees it (capture on document runs first).
    if (otherHeld && swapGift(btn.closest('.gfgFreeGiftMsgOptionRow'))) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    // Plain claim / unclaim — Kite still owns it; we only reconcile.
    // Spinner only for a fresh claim (not a re-click of the already-claimed
    // gift), but reconcile either way — the poll catches the count moving in
    // both directions (claim adds a line, unclaim removes one).
    if (!isThis) document.dispatchEvent(new CustomEvent('cart:adding'));
    reconcileAfterClaim();
  }, true);
})();
